import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useCohortListQuery } from '@verantos-dev/data-access-cohorts';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import type { CohortPickerOption, UseCohortPickerOptionsResult } from '../types';

// Fetches the project's cohort list and derives which cohorts are selectable
// and which of those match the current search query. Isolated from selection
// and confirm state so the list/search behavior can be tested independently.
export const useCohortPickerOptions = (projectId: string): UseCohortPickerOptionsResult => {
  const intl = useIntl();
  const { data: cohortList, isLoading, isError } = useCohortListQuery({ projectId });

  // A non-PROCESSED cohort may be picked too — processing is blocked
  // server-side until every referenced cohort is PROCESSED, so the process gate
  // (not this picker) enforces that.
  // TODO: cleanup with asset_edit_while_in_use ff
  const allowDraftCohorts = Boolean(useFeatureFlagSplit('asset_edit_while_in_use'));

  const allOptions = useMemo<CohortPickerOption[]>(() => {
    if (!cohortList) return [];
    return cohortList
      .filter(
        (c) =>
          allowDraftCohorts ||
          (c.latestStatus === CohortDefinitionStatus.PROCESSED && c.latestSubjectCount > 0)
      )
      .map((c) => ({
        cohortProfileId: c.cohortProfileId,
        cohortName: c.cohortName,
        latestStatus: c.latestStatus,
        latestSubjectCount: c.latestSubjectCount,
      }));
  }, [cohortList, allowDraftCohorts]);

  const [searchQuery, setSearchQuery] = useState('');
  const onSearchChange = useCallback((next: string) => setSearchQuery(next), []);

  const options = useMemo<CohortPickerOption[]>(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return allOptions;
    return allOptions.filter((o) => o.cohortName.toLowerCase().includes(q));
  }, [allOptions, searchQuery]);

  const emptyMessage =
    !isLoading && !isError && allOptions.length === 0
      ? intl.formatMessage({ id: 'analysisDetail.addCohortModal.empty' })
      : null;

  const noResultsMessage =
    !isLoading && !isError && allOptions.length > 0 && options.length === 0
      ? intl.formatMessage({ id: 'analysisDetail.addCohortModal.noResults' })
      : null;

  return {
    cohortList: cohortList ?? [],
    allOptions,
    options,
    searchQuery,
    onSearchChange,
    isLoading,
    isError,
    emptyMessage,
    noResultsMessage,
  };
};
